import { formatDate, parseDate } from "@sembark-travel/datetime-utils"
import { IUser } from "../Auth"
import { THotelNote } from "../HotelNotes"
import type { IHotel } from "./../Hotels"
import type { IMealPlan } from "./../MealPlans"
import type { IRoomType } from "./../RoomTypes"

export interface IHotelPrice {
	id: number
	hotel_id: number
	base_price: number
	persons: number
	adult_with_extra_bed_price: number
	child_with_extra_bed_price: number
	child_without_extra_bed_price: number
	start_date: string
	start_date_local?: string
	end_date: string
	end_date_local?: string
	meal_plan_id: number
	room_type_id: number
	hotel: IHotel
	meal_plan: IMealPlan
	room_type: IRoomType
	currency: string
	created_at: string
	created_by?: IUser
	is_booking_price: boolean
}

export function getOrderedUniqueHotelNotes(
	attachedNotes?: (THotelNote & { dates: Date[] })[],
	mealIds?: number[],
	roomTypeIds?: number[],
	excludeInternalNotes = false
): Record<string, string[]> {
	// One pass: filter + prepare key parts
	const prepared =
		attachedNotes?.flatMap((note) => {
			// Prepare key parts directly
			const parts: string[] = []
			let level = 0
			// Dates
			if (note.dates?.length) {
				const formattedDates = note.dates
					.sort((a, b) => a.getTime() - b.getTime())
					.map((d) => formatDate(d, "Do MMM"))
				parts.push(formattedDates.join(", "))
				level = 1
			}

			// Room Types
			if (note.room_types?.length) {
				const roomNames = note.room_types
					.filter((room) => roomTypeIds?.includes(room.id))
					.map((room) => room.name)
				if (roomNames.length) {
					parts.push(roomNames.join(", "))
					if (level === 0) level = 2
				}
			}

			// Meal Plans
			if (note.meal_plans?.length) {
				const mealNames = note.meal_plans
					.filter((meal) => mealIds?.includes(meal.id))
					.map((meal) => meal.name)
				if (mealNames.length) {
					parts.push(mealNames.join(", "))
					if (level === 0) level = 3
				}
			}

			let key = parts.length ? parts.join(", ") : "Hotel Note"

			let isInternal = false
			if (note.show_in_quote === 0 && note.show_in_voucher === 0) {
				if (excludeInternalNotes) {
					return [] // exclude it this note
				}
				key = key + "(Internal)"
				isInternal = true
			} else if (!excludeInternalNotes) {
				key = key + "(Shareable)"
			}

			const priorityLevel = (isInternal ? 0 : 10) + level
			return [{ key, description: note.description, priorityLevel }]
		}) ?? []

	// Group by key
	const grouped = prepared.reduce<
		Record<string, { descriptions: string[]; priority: number }>
	>((map, { key, description, priorityLevel }) => {
		if (!map[key]) map[key] = { descriptions: [], priority: priorityLevel }
		map[key].descriptions.push(description)
		return map
	}, {})

	// sort keys by priority (and by earliest date if priority ties)
	return Object.fromEntries(
		Object.entries(grouped)
			.sort(([keyA, a], [keyB, b]) => {
				if (a.priority !== b.priority) {
					return a.priority - b.priority
				}

				// if both are date-based (priority 1x range), compare first date
				const extractFirstDate = (key: string): number | null => {
					const match = key.match(/^(\d{1,2})(st|nd|rd|th) (\w{3})/)

					if (match) {
						const day = match[1]
						const month = match[3]
						const year = new Date().getFullYear()

						const d = parseDate(`${day} ${month} ${year}`)
						return d.getTime()
					}
					return null
				}

				const dateA = extractFirstDate(keyA)
				const dateB = extractFirstDate(keyB)

				if (dateA !== null && dateB !== null) {
					return dateA - dateB
				}

				// fallback: alphabetical if not date-based
				return keyA.localeCompare(keyB)
			})
			.map(([key, { descriptions }]) => [key, descriptions])
	)
}
